import { useCallback, useMemo, useRef, useState } from 'react'

export function isFileDrag(event) {
  const dataTransfer = event?.dataTransfer
  if (!dataTransfer) return false

  const types = Array.from(dataTransfer.types || [])
  if (types.length > 0) return types.includes('Files')

  const items = Array.from(dataTransfer.items || [])
  if (items.length > 0) return items.some(item => item.kind === 'file')

  return Boolean(dataTransfer.files?.length)
}

export function useDragDepth() {
  const [dragActive, setDragActive] = useState(false)
  const dragDepthRef = useRef(0)

  const reset = useCallback(() => {
    dragDepthRef.current = 0
    setDragActive(false)
  }, [])

  const onDragEnter = useCallback((event) => {
    if (!isFileDrag(event)) return

    event.preventDefault?.()
    event.stopPropagation?.()
    dragDepthRef.current += 1
    setDragActive(true)
  }, [])

  const onDragOver = useCallback((event) => {
    if (!isFileDrag(event)) return

    event.preventDefault?.()
    event.stopPropagation?.()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
  }, [])

  const onDragLeave = useCallback((event) => {
    if (dragDepthRef.current === 0) return

    event.preventDefault?.()
    event.stopPropagation?.()
    dragDepthRef.current -= 1
    if (dragDepthRef.current === 0) setDragActive(false)
  }, [])

  const onDrop = useCallback((event) => {
    if (dragDepthRef.current > 0 || isFileDrag(event)) {
      event.preventDefault?.()
      event.stopPropagation?.()
    }
    reset()
  }, [reset])

  const rootDragHandlers = useMemo(() => ({
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd: reset,
  }), [onDragEnter, onDragLeave, onDragOver, onDrop, reset])

  return { dragActive, rootDragHandlers, reset }
}
